import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import moment from "moment";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";
import AdminOrderTimeline from "../../../../components/Admin/InvestorPortal/AdminOrderTimeline";
import InvestorPortalDetailHeader from "../../../../components/Admin/InvestorPortal/InvestorPortalDetailHeader";
import {
  DetailSection,
  DetailField,
  DetailFieldList,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalDetailFields";
import InvestorPortalActionButton, {
  InvestorPortalActionStack,
} from "../../../../components/Admin/InvestorPortal/InvestorPortalActionButton";
import { OrderStatusBadge } from "../../../../components/Admin/InvestorPortal/AdminStatusBadge";
import { formatMoney } from "../../../../utils/currencyFormat";
import { convertToTitleCase } from "../../../../utils/helpers";
import {
  getOrderLifecycleActions,
  getRecommendedOrderAction,
} from "../../../../store/admin/marketplace/orderLifecycle";
import {
  STAFF_MARKETPLACE_ORDER_ACTIONS,
  useGetStaffMarketplaceOrderQuery,
  useStaffMarketplaceOrderLifecycleMutation,
  useAddStaffMarketplaceOrderNoteMutation,
  useAssignStaffMarketplaceOrderMutation,
} from "../../../../store/admin/marketplace/staffMarketplaceApi";
import { useGetStaffListQuery } from "../../../../store/admin/staff";
import { ORDER_STATUS } from "../../../../store/admin/marketplace/constants";
import { withMarketplaceGuard } from "../withInvestorPortalGuard";

const ORDER_TARGET_TO_ACTION = {
  [ORDER_STATUS.AWAITING_CONFIRMATION]: STAFF_MARKETPLACE_ORDER_ACTIONS.awaitingConfirmation,
  [ORDER_STATUS.CONFIRMED]: STAFF_MARKETPLACE_ORDER_ACTIONS.confirm,
  [ORDER_STATUS.PROCESSING]: STAFF_MARKETPLACE_ORDER_ACTIONS.startProcessing,
  [ORDER_STATUS.READY_FOR_PICKUP]: STAFF_MARKETPLACE_ORDER_ACTIONS.readyForPickup,
  [ORDER_STATUS.SHIPPED]: STAFF_MARKETPLACE_ORDER_ACTIONS.ship,
  [ORDER_STATUS.DELIVERED]: STAFF_MARKETPLACE_ORDER_ACTIONS.deliver,
  [ORDER_STATUS.COMPLETED]: STAFF_MARKETPLACE_ORDER_ACTIONS.complete,
};

function RecommendedActionCard({ status }) {
  const rec = getRecommendedOrderAction(status);
  return (
    <DetailSection title="Recommended next action">
      <p className="fw-semibold mb-1" style={{ color: "#383838", fontSize: 14 }}>
        {rec.label}
      </p>
      <p className="text-muted small mb-0">{rec.description}</p>
    </DetailSection>
  );
}

function OrderDetailPage() {
  const { orderId } = useParams();
  const orderNumber = orderId;
  const navigate = useNavigate();
  const [noteText, setNoteText] = useState("");
  const [reasonModal, setReasonModal] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [assignStaffId, setAssignStaffId] = useState("");

  const { data: order, isFetching, isError } = useGetStaffMarketplaceOrderQuery(orderNumber, {
    skip: !orderNumber,
  });
  const [lifecycleMutation, { isLoading: isActionLoading }] =
    useStaffMarketplaceOrderLifecycleMutation();
  const [addNote, { isLoading: isNoteLoading }] = useAddStaffMarketplaceOrderNoteMutation();
  const [assignStaff, { isLoading: isAssignLoading }] = useAssignStaffMarketplaceOrderMutation();
  const { data: staffListData } = useGetStaffListQuery(1);
  const staffOptions = useMemo(() => staffListData?.staffs || [], [staffListData?.staffs]);

  const actions = useMemo(() => getOrderLifecycleActions(order?.status), [order?.status]);

  useEffect(() => {
    if (order?.assignedStaffId != null) {
      setAssignStaffId(String(order.assignedStaffId));
    }
  }, [order?.assignedStaffId]);

  if (isFetching && !order) {
    return (
      <div className="pb-4">
        <TableSkeletonLoader rows={8} columns={2} />
      </div>
    );
  }

  if (!order || isError) {
    return <Navigate to="/super_admin/investor_portal/marketplace/orders" replace />;
  }

  const runAction = async (action, label, body) => {
    try {
      await lifecycleMutation({
        orderNumber: order.orderNumber || orderNumber,
        action,
        body,
      }).unwrap();
      toast.success(label);
      if (action === STAFF_MARKETPLACE_ORDER_ACTIONS.confirm) {
        toast("Stock was decremented for this order’s line items.", { icon: "ℹ️" });
      }
      if (
        (action === STAFF_MARKETPLACE_ORDER_ACTIONS.decline ||
          action === STAFF_MARKETPLACE_ORDER_ACTIONS.cancel) &&
        order.stockReserved
      ) {
        toast("Reserved stock was released.", { icon: "ℹ️" });
      }
      setReasonModal(null);
      setReasonText("");
      setConfirm(null);
    } catch (error) {
      toast.error(error?.data?.message || "Action failed.");
    }
  };

  const handleLifecycleClick = (actionDef) => {
    if (actionDef.action === "decline" || actionDef.action === "cancel") {
      setReasonModal({
        type: actionDef.action,
        label: actionDef.label,
        action:
          actionDef.action === "decline"
            ? STAFF_MARKETPLACE_ORDER_ACTIONS.decline
            : STAFF_MARKETPLACE_ORDER_ACTIONS.cancel,
      });
      setReasonText("");
      return;
    }

    const action = ORDER_TARGET_TO_ACTION[actionDef.target];
    if (!action) {
      toast.error("This action is not available.");
      return;
    }

    const needsStockConfirm =
      action === STAFF_MARKETPLACE_ORDER_ACTIONS.confirm ||
      action === STAFF_MARKETPLACE_ORDER_ACTIONS.startProcessing;

    setConfirm({
      title: actionDef.label,
      message: needsStockConfirm
        ? `${actionDef.label} for ${order.orderNumber}? Confirm stock availability before continuing.`
        : `Are you sure you want to ${actionDef.label.toLowerCase()}?`,
      confirmText: "Yes, continue",
      confirmButtonClass:
        actionDef.variant === "danger" ? "bg-danger text-white" : "bg-primary1 text-white",
      action: () => runAction(action, actionDef.label),
    });
  };

  const submitReasonAction = async () => {
    if (!reasonModal) return;
    if (!reasonText.trim()) {
      toast.error("A reason is required.");
      return;
    }
    await runAction(reasonModal.action, reasonModal.label, { reason: reasonText.trim() });
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await addNote({
        orderNumber: order.orderNumber || orderNumber,
        note: noteText.trim(),
      }).unwrap();
      setNoteText("");
      toast.success("Note added");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to add note.");
    }
  };

  const handleAssignStaff = async () => {
    if (!assignStaffId) {
      toast.error("Select a staff member to assign.");
      return;
    }
    try {
      await assignStaff({
        orderNumber: order.orderNumber || orderNumber,
        assigned_staff_id: Number(assignStaffId),
      }).unwrap();
      toast.success("Staff assigned");
    } catch (error) {
      toast.error(error?.data?.message || "Unable to assign staff.");
    }
  };

  const staffLabel = (s) => {
    const name =
      [s?.first_name, s?.last_name].filter(Boolean).join(" ") ||
      s?.full_name ||
      s?.name ||
      s?.email ||
      `Staff #${s?.id}`;
    return name;
  };

  const deliveryAddress =
    order.deliveryAddress && typeof order.deliveryAddress === "object"
      ? [
          order.deliveryAddress.line1,
          order.deliveryAddress.line2,
          order.deliveryAddress.city,
          order.deliveryAddress.state,
          order.deliveryAddress.postal_code,
        ]
          .filter(Boolean)
          .join(", ")
      : order.deliveryAddress || "—";

  return (
    <div className="pb-4">
      <InvestorPortalDetailHeader
        title="Order"
        highlight={order.orderNumber}
        onBack={() => navigate("/super_admin/investor_portal/marketplace/orders")}
      />

      <div className="row g-3 investor-portal-detail-layout">
        <div className="col-lg-8">
          <DetailSection>
            <div className="investor-portal-status-row mb-3">
              <OrderStatusBadge status={order.status} />
              {order.stockReserved ? (
                <span
                  className="badge rounded-pill px-3 py-2"
                  style={{ backgroundColor: "#31778C", color: "#fff" }}
                >
                  Stock reserved
                </span>
              ) : null}
            </div>

            <h5 className="fw-bold mb-1" style={{ color: "#383838" }}>
              {order.customerName || order.applicantName || "Customer"}
            </h5>
            <p className="text-muted small mb-4">
              Submitted{" "}
              {order.submittedAt ? moment(order.submittedAt).format("DD-MMM-YYYY HH:mm") : "—"}
            </p>

            <DetailFieldList>
              <DetailField label="Email" value={order.email || "—"} />
              <DetailField label="Phone" value={order.phone || "—"} />
              <DetailField
                label="Account type"
                value={
                  convertToTitleCase(
                    String(order.customerType || order.accountType || "").replace(/_/g, " ")
                  ) || "—"
                }
              />
              <DetailField
                label="Checkout mode"
                value={
                  convertToTitleCase(String(order.checkoutMode || "").replace(/_/g, " ")) || "—"
                }
              />
              <DetailField
                label="Payment status"
                value={
                  convertToTitleCase(String(order.paymentStatus || "").replace(/_/g, " ")) || "—"
                }
              />
              {order.checkoutMode === "pay_online" && order.paymentStatus === "pending" ? (
                <DetailField
                  label="Payment note"
                  value="Awaiting online payment — staff confirm is unavailable until the customer pays."
                />
              ) : null}
              {order.paymentReference ? (
                <DetailField label="Payment reference" value={order.paymentReference} />
              ) : null}
              {order.stockHoldExpiresAt ? (
                <DetailField
                  label="Stock hold expires"
                  value={moment(order.stockHoldExpiresAt).format("DD-MMM-YYYY HH:mm")}
                />
              ) : null}
              {order.paymentTotal != null ? (
                <DetailField label="Payment total" value={formatMoney(order.paymentTotal)} />
              ) : null}
              {order.paymentConfirmedAt ? (
                <DetailField
                  label="Payment confirmed"
                  value={moment(order.paymentConfirmedAt).format("DD-MMM-YYYY HH:mm")}
                />
              ) : null}
              <DetailField
                label="Delivery method"
                value={
                  convertToTitleCase(String(order.deliveryMethod || "").replace(/_/g, " ")) || "—"
                }
              />
              <DetailField label="Delivery address" value={deliveryAddress} />
              <DetailField label="Pickup location" value={order.pickupLocation || "—"} />
              <DetailField label="Customer note" value={order.customerNote || "—"} />
              <DetailField label="Assigned staff">
                <div className="d-flex flex-column gap-2">
                  <select
                    className="form-select form-select-sm"
                    value={assignStaffId}
                    onChange={(e) => setAssignStaffId(e.target.value)}
                    aria-label="Assign staff"
                  >
                    <option value="">Unassigned</option>
                    {staffOptions.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {staffLabel(s)}
                      </option>
                    ))}
                  </select>
                  <InvestorPortalActionButton
                    btnText={isAssignLoading ? "Assigning..." : "Assign staff"}
                    variant="outline"
                    fullWidth={false}
                    disabled={!assignStaffId || isAssignLoading}
                    onClick={handleAssignStaff}
                  />
                </div>
              </DetailField>
              <DetailField label="Subtotal">
                <span className="fw-semibold">{formatMoney(order.subtotal)}</span>
              </DetailField>
              <DetailField label="Delivery fee">
                <span className="fw-semibold">{formatMoney(order.deliveryFee)}</span>
              </DetailField>
              <DetailField label="Total">
                <span className="fw-bold">{formatMoney(order.totalAmount ?? order.total)}</span>
              </DetailField>
            </DetailFieldList>
          </DetailSection>

          <DetailSection title="Line items">
            {(order.items || []).length === 0 ? (
              <p className="text-muted small mb-0">No items.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="text-center">Qty</th>
                      <th className="text-end">Unit</th>
                      <th className="text-end">Line total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((item) => (
                      <tr key={item.id || `${item.productId}-${item.sku}`}>
                        <td>
                          <span className="fw-semibold">{item.productName}</span>
                          {item.sku ? (
                            <span className="text-muted small d-block">SKU {item.sku}</span>
                          ) : null}
                        </td>
                        <td className="text-center">{item.quantity}</td>
                        <td className="text-end">{formatMoney(item.unitPrice)}</td>
                        <td className="text-end fw-semibold">{formatMoney(item.lineTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DetailSection>

          <DetailSection title="Timeline">
            <AdminOrderTimeline entries={order.timeline || []} />
          </DetailSection>

          <DetailSection title="Staff notes">
            {order.staffNotes ? (
              <div className="investor-portal-note-card mb-3">
                <p className="small mb-0" style={{ whiteSpace: "pre-wrap" }}>
                  {typeof order.staffNotes === "string"
                    ? order.staffNotes
                    : Array.isArray(order.staffNotes)
                      ? order.staffNotes
                          .map((n) => n.text || n.note || "")
                          .filter(Boolean)
                          .join("\n")
                      : JSON.stringify(order.staffNotes)}
                </p>
              </div>
            ) : (
              <p className="text-muted small mb-3">No notes yet.</p>
            )}
            <div className="d-flex flex-column flex-sm-row gap-2 mt-2">
              <input
                className="form-control investor-portal-note-input flex-grow-1"
                placeholder="Add note..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && noteText.trim()) handleAddNote();
                }}
              />
              <InvestorPortalActionButton
                btnText="Add note"
                variant="primary"
                fullWidth={false}
                disabled={!noteText.trim() || isNoteLoading}
                onClick={handleAddNote}
              />
            </div>
          </DetailSection>
        </div>

        <div className="col-lg-4">
          <RecommendedActionCard status={order.status} />
          <DetailSection title="Actions">
            <InvestorPortalActionStack>
              {actions.length === 0 ? (
                <p className="text-muted small mb-0">No further actions for this status.</p>
              ) : (
                actions.map((action) => (
                  <InvestorPortalActionButton
                    key={action.label}
                    btnText={action.label}
                    variant={action.variant || "outline"}
                    disabled={isActionLoading}
                    onClick={() => handleLifecycleClick(action)}
                  />
                ))
              )}
            </InvestorPortalActionStack>
          </DetailSection>
        </div>
      </div>

      <ConfirmationModal
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.action}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText}
        confirmButtonClass={confirm?.confirmButtonClass}
        isLoading={isActionLoading}
      />

      <ConfirmationModal
        isOpen={Boolean(reasonModal)}
        onClose={() => {
          setReasonModal(null);
          setReasonText("");
        }}
        onConfirm={submitReasonAction}
        title={reasonModal?.label || "Provide reason"}
        message={
          <div>
            <p className="mb-2">Please provide a reason for this action.</p>
            <textarea
              className="form-control"
              rows={3}
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Reason..."
            />
          </div>
        }
        confirmText="Confirm"
        confirmButtonClass="bg-danger text-white"
        isLoading={isActionLoading}
      />
    </div>
  );
}

export default withMarketplaceGuard(OrderDetailPage);
